import { useState } from "react";
import { toast } from "sonner";
import { Bell, BellRing, CheckCheck, AlertTriangle, Send } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { fmtDateTime, fmtRange } from "@/lib/format";

export const sendNotify = async (eventId, ids, t, reload) => {
  try {
    const r = await api.post(`/events/${eventId}/notify-assignments`, { assignment_ids: ids });
    toast.success(t("nt.sent").replace("{n}", r.data.sent));
    reload();
  } catch (e) {
    toast.error(errorText(t, e));
  }
};

export const NotifyState = ({ a }) => {
  const { t, lang } = useLang();
  if (!a.notified_at) return <span className="text-[10px] uppercase tracking-wider text-neutral-500" data-testid={`notify-state-${a.id}`} title={t("nt.never")}>{t("nt.neverShort")}</span>;
  return (
    <span data-testid={`notify-state-${a.id}`} title={`${t("nt.last")} ${fmtDateTime(a.notified_at, lang)}`}
      className={`flex items-center gap-1 text-[10px] uppercase tracking-wider ${a.changed ? "text-amber-500" : "text-emerald-500"}`}>
      {a.changed ? <BellRing className="w-3 h-3" /> : <CheckCheck className="w-3 h-3" />}{a.changed ? t("nt.changedShort") : t("nt.sentShort")}
    </span>
  );
};

export const NotifyButton = ({ a, eventId, reload }) => {
  const { t } = useLang();
  return (
    <button data-testid={`notify-assignment-${a.id}`} title={t("nt.notify")} aria-label={t("nt.notify")}
      onClick={(e) => { e.stopPropagation(); sendNotify(eventId, [a.id], t, reload); }}
      className="press p-1 text-neutral-400 hover:text-[#D9B872]"><Bell className="w-3.5 h-3.5" /></button>
  );
};

export const ConflictIcon = ({ a }) => {
  const { t } = useLang();
  if (!a.conflicts?.length) return null;
  const tip = a.conflicts.map((c) => `${c.event_name} ${fmtRange(c.start, c.end, t)}`).join(" · ");
  return <span data-testid={`conflict-flag-${a.id}`} title={`${t("b.conflict")} : ${tip}`} className="text-red-500"><AlertTriangle className="w-3.5 h-3.5" /></span>;
};

export const NotifyTeamButton = ({ eventId, count, ids, reload, testid = "notify-team-btn", label }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={testid} disabled={!count} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="press inline-flex items-center gap-1.5 rounded-full border border-[#C5A059]/50 px-3 py-1.5 text-xs text-[#D9B872] hover:bg-[#C5A059]/10 disabled:opacity-40">
        <Send className="w-3.5 h-3.5" />{label || t("nt.team")} <span className="mono">({count})</span>
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-2xl" data-testid={`${testid}-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{label || t("nt.team")}</AlertDialogTitle>
            <AlertDialogDescription>{t("nt.confirmTeam").replace("{n}", count)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid={`${testid}-cancel`}>{t("c.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-[#C5A059] text-[#0A0A0C] hover:bg-[#D9B872]" data-testid={`${testid}-confirm`}
              onClick={() => sendNotify(eventId, ids ?? null, t, reload)}>{t("nt.send")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
